import type { JSX } from "react";
import numberRenderer from "../../common/number";
import phoneRenderer from "../../common/phone";
import switchRenderer from "../../common/switch";
import duiNitRenderer from "../../common/dui-nit";
import datetimeRangeRenderer from "../../common/datetime-range";
import selectRenderer from "../../common/select";
import tableRenderer from "../../common/table";
import filteredSelectRenderer from "../../common/filtered-select";
import fileRenderer from "../../common/file";
import type { Fields } from "../../form-correction-table";
import Interoperability from "./Interoperability";
import ValueDisplay from "../../common/value-display";

function parseJsonValue(rawValue: unknown): unknown {
  if (
    typeof rawValue === "string" &&
    (rawValue.startsWith("[") || rawValue.startsWith("{"))
  ) {
    try {
      return JSON.parse(rawValue);
    } catch {
      /* fall through */
    }
  }
  return rawValue;
}

const renderers: Record<string, (p: Props) => JSX.Element | null> = {
  interoperability: ({ field, value, filledFieldId, isOldValue }) => (
    <Interoperability
      field={field}
      filledFieldId={filledFieldId}
      value={value as Record<string, string>}
      isOldValue={isOldValue}
    />
  ),
  datetime_range: (p) =>
    datetimeRangeRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  date_range: (p) =>
    datetimeRangeRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  table: (p) =>
    tableRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  dui_nit: (p) =>
    duiNitRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  select: (p) =>
    selectRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  multiselect: (p) =>
    selectRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  list: (p) =>
    selectRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  filtered_select: (p) =>
    filteredSelectRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  "filtered-list": (p) =>
    filteredSelectRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  filtered_list: (p) =>
    filteredSelectRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  switch: (p) =>
    switchRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  file: (p) =>
    fileRenderer({
      obj: p.field,
      value: p.value,
      targetField: {
        id: p.filledFieldId,
        value: undefined,
        updated_at: "",
        files: p.field?.filled_fields?.find((ff) => ff.id === p.filledFieldId)
          ?.files,
      },
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  phone: (p) =>
    phoneRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
  number: (p) =>
    numberRenderer({
      obj: p.field,
      value: p.value,
      targetField: undefined,
      fieldArr: null,
      isOldValue: p.isOldValue,
    }),
};

type Props = Readonly<{
  filledFieldId: string;
  field: Fields;
  value: unknown;
  isOldValue?: boolean;
}>;

export default function FieldValue({ field, value: rawValue, ...rest }: Props) {
  const value = parseJsonValue(rawValue);
  const renderer = renderers[field.type];
  if (renderer) return renderer({ field, value, ...rest }) ?? <>{null}</>;

  let text = "—";
  if (value && typeof value === "string") {
    text = value;
  } else if (Array.isArray(value)) {
    text = value.join(", ");
  } else if (value && typeof value === "object") {
    text = JSON.stringify(value);
  }
  return <ValueDisplay isOldValue={rest.isOldValue}>{text}</ValueDisplay>;
}
